import { SelectControl } from "@shared/components/form";
import ModalBlank from "@shared/components/modals/ModalBlank";
import { Button } from "@shared/components/ui/Button";
import React, { useEffect, useState } from "react";
import { Invitation, UserOption } from "../types/invitation";

interface DeleteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (transferToUserId: string) => void;
  isLoading: boolean;
  availableUsers: UserOption[];
  deletedInvitation: Invitation;
}

export const DeleteUserModal: React.FC<DeleteUserModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  availableUsers,
  deletedInvitation,
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [error, setError] = useState<string>("");

  // Reset state when modal opens or closes
  useEffect(() => {
    if (isOpen) {
      setSelectedUserId("");
      setError("");
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (!selectedUserId) {
      setError("Please select a user to transfer content to");
      return;
    }
    onConfirm(selectedUserId);
  };

  return (
    <ModalBlank
      id="delete-user-modal"
      modalOpen={isOpen}
      setModalOpen={onClose}
      className="max-w-lg"
      showCloseButton={true}
      closeOnOutClick={false}
    >
      <div className="p-6">
        <div className="flex items-center justify-center mb-5">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-600 dark:text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              ></path>
            </svg>
          </div>
        </div>

        <h3 className="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
          Delete User
        </h3>

        <p className="text-center text-gray-500 dark:text-gray-400 mb-6">
          You are about to delete the user{" "}
          <strong>{deletedInvitation?.email}</strong>. This action cannot be
          undone.
        </p>

        <div className="mb-6">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
            What will be deleted:
          </h4>
          <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400">
            <li>User account and authentication access</li>
            <li>User invitation</li>
          </ul>
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
            Content that will be transferred:
          </h4>
          <ul className="list-disc pl-5 text-gray-600 dark:text-gray-400">
            <li>Spaces and Space Comments</li>
            <li>Resources</li>
            <li>Calendar Events and Event Comments</li>
            <li>Posts and Post Comments</li>
          </ul>
        </div>

        <div className="mb-6">
          <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2">
            Transfer content to:
          </label>
          <SelectControl
            id="transferToUser"
            name="transferToUser"
            label="Select User"
            value={selectedUserId}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setError("");
            }}
            options={availableUsers.map((user) => ({
              value: user.id,
              label: user.displayName,
            }))}
            placeholder="Select a user"
            disabled={isLoading}
          />
          {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>

        <div className="flex justify-center space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleConfirm}
            isLoading={isLoading}
            loadingText="Deleting..."
          >
            Delete User
          </Button>
        </div>
      </div>
    </ModalBlank>
  );
};

export default DeleteUserModal;
